import React, {
  forwardRef,
  useState,
  useRef,
  useContext,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import Draggable from "react-draggable";
import cx from "classnames";

import LpgContinueSolid from "@/icons/Lpg/ContinueSolid";

import { mapChildrenIndexedByType } from "../../helper/map-children";

import { TabsContext } from "./TabsContext";
import { Icon } from "../Icon";

import styles from "./Tabs.scss";

function calcItemsFit(listWidth, itemsWidth) {
  return listWidth - itemsWidth >= 0;
}

const TabList = forwardRef((props, forwardedRef) => {
  const { children, initialItemsWidth, ...restProps } = props;
  const {
    controlledPosition,
    statePosition,
    onHold,
    onRelease,
    hideRightTab,
    hideLeftTab,
    updateNavigationState,
  } = useContext(TabsContext) || {};

  const [bounds, setBounds] = useState({ left: 0, right: 0 });
  const [itemsFit, setItemsFit] = useState(
    calcItemsFit(
      typeof document !== "undefined" ? document.body.clientWidth : 0,
      initialItemsWidth
    )
  );
  const wrapperRef = useRef();
  const listRef = useRef();
  const boundsRef = useRef();
  boundsRef.current = bounds;

  const updateItemsFit = () => {
    const listWidth = wrapperRef.current.offsetWidth;
    const listChildren = Array.from(listRef.current.children);
    const itemsWidth = listChildren.reduce(
      (totalWidth, child) => totalWidth + child.offsetWidth,
      0
    );
    const itemsFitNow = calcItemsFit(listWidth, itemsWidth);
    if (itemsFit !== itemsFitNow) {
      setItemsFit(itemsFitNow);
    }
  };

  const calculateBounds = () => {
    const { left: oldLeft, right: oldRight } = boundsRef.current;
    const newLeft = -(
      listRef.current.offsetWidth - wrapperRef.current.offsetWidth
    );
    const left = newLeft < 0 ? newLeft : 0;
    const right = 0;
    if (left !== oldLeft || right !== oldRight) {
      setBounds({ left, right });
    }
  };

  useEffect(() => {
    const resizeHandler = () => {
      calculateBounds();
      updateItemsFit();
      updateNavigationState();
    };

    resizeHandler();
    if (typeof window !== "undefined") {
      window.addEventListener("resize", resizeHandler);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", resizeHandler);
      }
    };
  });

  return (
    <div ref={forwardedRef}>
      <div className={styles.listWrapper} ref={wrapperRef}>
        <Draggable
          axis="x"
          bounds={bounds}
          position={{ x: controlledPosition, y: 0 }}
          positionOffset={{ x: 0, y: 0 }}
          onStop={statePosition}
          nodeRef={listRef}
        >
          <div
            className={cx(styles.list, {
              [styles.listCenter]: itemsFit,
            })}
            role="tablist"
            ref={listRef}
            {...restProps}
          >
            {mapChildrenIndexedByType(children, (child, tabIndex) =>
              React.cloneElement(child, { tabIndex })
            )}
          </div>
        </Draggable>
      </div>
      <div className={itemsFit ? styles.arrowWrapper : styles.arrowWrapperShow}>
        <button
          type="button"
          className={`${styles.arrowLeft} ${
            hideLeftTab ? styles.hideArrow : ""
          }`}
          onMouseDown={() => {
            onHold("ArrowLeft");
          }}
          onMouseUp={() => {
            onRelease("ArrowLeft");
          }}
          onTouchStart={() => {
            onHold("ArrowLeft");
          }}
          onTouchEnd={() => {
            onRelease("ArrowLeft");
          }}
        >
          <Icon icon={LpgContinueSolid} size={Icon.Size.small} />
        </button>
        <button
          type="button"
          className={`${styles.arrowRight} ${
            hideRightTab ? styles.hideArrow : ""
          }`}
          onMouseDown={() => {
            onHold("ArrowRight");
          }}
          onMouseUp={() => {
            onRelease("ArrowRight");
          }}
          onTouchStart={() => {
            onHold("ArrowRight");
          }}
          onTouchEnd={() => {
            onRelease("ArrowRight");
          }}
        >
          <Icon icon={LpgContinueSolid} size={Icon.Size.small} />
        </button>
      </div>
    </div>
  );
});

TabList.displayName = "Tabs.TabList";

TabList.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  initialItemsWidth: PropTypes.number,
};

TabList.defaultProps = {
  initialItemsWidth: Number.MAX_VALUE,
};

export default TabList;
